
import React from 'react';
import SectionLayout from '@/components/SectionLayout';
import CTAButton from '@/components/CTAButton';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Play, Sparkles, Zap, TrendingUp } from 'lucide-react';
const HeroSection = () => {
  return <SectionLayout paddingY="pt-12 sm:pt-20 md:pt-32 pb-8 sm:pb-12 md:pb-20" mobilePaddingY="pt-8 pb-6" backgroundColor="bg-gradient-to-br from-[#FEE6D8]/20 via-transparent to-[#003a70]/10" backgroundPattern="dots" patternOpacity={8} patternColor="#003a70" enableMagicBackground={true} decorativeElements={<>
          {/* Floating Icons */}
          <div className="absolute top-1/4 left-[10%] animate-float opacity-30 hidden sm:block">
            <div className="w-12 h-12 bg-[#FEE6D8]/40 rounded-full flex items-center justify-center backdrop-blur-sm">
              <Sparkles size={20} className="text-[#003a70]" />
            </div>
          </div>
          
          <div className="absolute top-1/3 right-[15%] animate-float opacity-40 hidden sm:block" style={{
      animationDelay: '1s'
    }}>
            <div className="w-10 h-10 bg-[#003a70]/20 rounded-full flex items-center justify-center backdrop-blur-sm">
              <Zap size={16} className="text-[#003a70]" />
            </div>
          </div>
          
          <div className="absolute bottom-1/3 left-[20%] animate-float opacity-35 hidden sm:block" style={{
      animationDelay: '2s'
    }}>
            <div className="w-14 h-14 bg-[#FEE6D8]/30 rounded-full flex items-center justify-center backdrop-blur-sm">
              <TrendingUp size={22} className="text-[#003a70]" />
            </div>
          </div>
          
          {/* Animated Gradient Orbs */}
          <div className="absolute top-[20%] right-[25%] w-32 h-32 bg-gradient-to-br from-[#FEE6D8]/30 to-[#003a70]/20 rounded-full blur-xl animate-pulse opacity-60 hidden sm:block"></div>
          <div className="absolute bottom-[25%] right-[10%] w-24 h-24 bg-gradient-to-br from-[#003a70]/20 to-[#FEE6D8]/30 rounded-full blur-2xl animate-float opacity-50 hidden sm:block" style={{
      animationDelay: '3s'
    }}></div>
          <div className="absolute top-[60%] left-[8%] w-20 h-20 bg-gradient-to-br from-[#FEE6D8]/40 to-transparent rounded-full blur-xl animate-pulse opacity-40 hidden sm:block" style={{
      animationDelay: '1.5s'
    }}></div>
          
          {/* Subtle Grid Lines */}
          <div className="absolute inset-0 opacity-5 hidden sm:block">
            <div className="absolute top-1/4 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#003a70] to-transparent"></div>
            <div className="absolute top-3/4 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FEE6D8] to-transparent"></div>
            <div className="absolute top-0 bottom-0 left-1/4 w-px bg-gradient-to-b from-transparent via-[#003a70]/50 to-transparent"></div>
            <div className="absolute top-0 bottom-0 right-1/4 w-px bg-gradient-to-b from-transparent via-[#FEE6D8]/80 to-transparent"></div>
          </div>
        </>}>
      <div className="max-w-5xl mx-auto text-center px-3 sm:px-4 md:px-6">
        {/* Attention-Grabbing Badge */}
        <div className="mb-6 sm:mb-8 md:mb-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#003a70] to-[#003a70]/90 text-white font-bold px-4 py-2 sm:px-6 sm:py-3 text-xs sm:text-sm md:text-base shadow-lg animate-pulse">
            <span className="w-2 h-2 bg-[#FEE6D8] rounded-full animate-ping"></span>
            Attention Local Agency Owners
          </div>
        </div>
        
        {/* Main Headline - Optimized for mobile */}
        <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-[#003a70] leading-tight mb-4 sm:mb-6 md:mb-8 max-w-5xl mx-auto">For Agency Owners Who LOVE Delivering Results (But Dread Prospecting): The Simple 'Problem-Solver' Service That Makes Getting New Clients Feel Effortless</h1>
        
        {/* Sub-headline with Design Flair */}
        <div className="mb-6 sm:mb-8 md:mb-10 max-w-3xl mx-auto">
          <div className="relative">
            {/* Decorative background elements */}
            <div className="absolute -inset-2 sm:-inset-4 bg-gradient-to-r from-[#FEE6D8]/20 via-[#FEE6D8]/30 to-[#FEE6D8]/20 rounded-lg sm:rounded-2xl blur-xl"></div>
            <div className="absolute -inset-1 sm:-inset-2 bg-gradient-to-r from-[#003a70]/5 via-[#003a70]/10 to-[#003a70]/5 rounded-lg sm:rounded-xl"></div>
            
            {/* Main container */}
            <div className="relative bg-[#FEE6D8]/80 backdrop-blur-sm border border-[#FEE6D8]/50 rounded-lg sm:rounded-xl px-4 py-3 sm:px-6 sm:py-4 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]">
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl text-[#003a70] leading-relaxed italic font-bold">(Even If You Suck at Selling!)</p>
              
              {/* Decorative corner elements */}
              <div className="absolute top-2 right-2 w-2 h-2 sm:w-3 sm:h-3 bg-[#FEE6D8] rounded-full opacity-60"></div>
              <div className="absolute bottom-2 left-2 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#003a70]/30 rounded-full"></div>
            </div>
          </div>
        </div>
        
        {/* Video Section */}
        <div className="mb-6 sm:mb-8 md:mb-10 lg:mb-12">
          <div className="w-full max-w-4xl mx-auto">
            <div className="relative rounded-lg sm:rounded-xl md:rounded-2xl overflow-hidden shadow-xl sm:shadow-2xl border border-[#FEE6D8]/30 sm:border-2">
              <div className="w-full h-full" dangerouslySetInnerHTML={{
              __html: `<div style="position: relative; padding-bottom: 61.36363636363637%; height: 0;"><iframe src="https://www.loom.com/embed/9c257419bb594aa7914d99764d61c639?sid=3b20ae81-a130-4a3e-939b-cfb6c9e3ea79&hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true" frameborder="0" webkitallowfullscreen mozallowfullscreen allowfullscreen style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"></iframe></div>`
            }} />
            </div>
          </div>
        </div>
        
        {/* CTA Button */}
        <div className="mt-6 sm:mt-8 md:mt-10">
          <CTAButton variant="primary" className="text-base sm:text-lg md:text-xl px-6 sm:px-8 md:px-12 py-3 sm:py-4 md:py-5 w-full sm:w-auto bg-gradient-to-r from-[#003a70] to-[#003a70]/90 hover:from-[#003a70]/90 hover:to-[#003a70] shadow-xl sm:shadow-2xl transform hover:scale-105 transition-all duration-300 border border-[#FEE6D8]/30" to="https://cart.deliciousmarketing.io/local-ai-assistant-launchpad/?coupon=AI-LAUNCHPAD">
            Get Instant Access Now
          </CTAButton>
          
          {/* Trust indicators under button */}
          <div className="mt-3 sm:mt-4 text-sm text-gray-600 space-y-1">
            
          </div>
        </div>
      </div>
    </SectionLayout>;
};
export default HeroSection;
